<template>
  <div>
    <div>
      <h1
        class="mb-5 text--accent-2"
        v-html="currentPlanName"
      />
    </div>

    <v-row class="mb-5">
      <v-col
          v-for="plan in plans"
          :key="`dolphin-anty-user-panel-plan-${plan.name}`"
          cols="12"
          sm="6"
          md="3"
      >
        <v-card class="user-panel-plans-card rounded-xl">
          <v-card-text
              style="height: 100%;"
              class="d-flex flex-column justify-space-between"
          >
            <div>
              <div class="user-panel-plans-card-title mb-4">
                {{ plan.name }}
              </div>

              <div class="mb-4">
                {{ plan.description }}
              </div>

              <div class="mb-1">
                <span style="font-size: 32px; color: #000000;" class="mr-2">
                  {{
                    plan.ammountPerMonth.toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD',
                      maximumFractionDigits: 0,
                      minimumFractionDigits: 0
                    })
                  }}
                </span>
                <span
                    v-if="plan.ammountPerMonth !== 0 && $i18n.locale === 'ru'"
                    style="font-size: 24px; font-weight: 300;"
                >
                  {{
                    convertFromUsd(plan.ammountPerMonth, 'RUB').toLocaleString(
                        'ru-RU',
                        {
                          style: 'currency',
                          currency: 'RUB',
                          maximumFractionDigits: 0,
                          minimumFractionDigits: 0
                        }
                    )
                  }}
                </span>
              </div>
              <div>
                {{ $t('plans.perMonth').toLowerCase() }}
              </div>

              <v-divider class="my-4" />

              <div
                v-if="plan.isCustom"
                class="mb-2"
              >
                <div class="mb-4">
                  {{ $tc('plans.freeCustom.browserProfiles', profile.subscription.browserProfiles.limit, {count: profile.subscription.browserProfiles.limit}) }}
                </div>
                <div>
                  {{ $tc('plans.freeCustom.users', profile.subscription.browserProfiles.limit, {count: profile.subscription.users.limit}) }}
                </div>
              </div>
              <div v-else-if="plan.name === 'Enterprise'" class="mb-2">
                {{ $t('plans.saveUpTo') + ':' }}
                <v-combobox
                    v-model="profilesCountSelected"
                    :items="profilesCount"
                    dense
                    outlined
                    hide-details
                    rounded
                    class="user-panel-plans-card-profiles-count-autocomplete"
                />
              </div>
              <div v-else class="mb-2">
                {{
                  $t('plans.saveUpTo') +
                  ' ' +
                  plan.count +
                  ' ' +
                  $t('plans.browserProfiles').toLowerCase()
                }}
              </div>

              <div
                v-if="isFreeTariff(plan.name) && !plan.isCustom"
                class="mb-2"
              >
                {{ $t('plans.free.limitOnCreating') }}
              </div>

              <ul
                v-if="!plan.isCustom"
                class="mb-4 user-panel-plans-card-features"
              >
                <li
                    v-for="(feature, featureIndex) in plan.features"
                    :key="`dolphin-anty-user-panel-plans-feature-${featureIndex}`"
                >
                  <v-icon :size="12" color="primary" class="mr-1">
                    fas fa-plus
                  </v-icon>
                  {{ feature }}
                </li>
              </ul>
            </div>

            <div v-if="plan.isCustom">
              <v-btn
                :id="`buttonTryCustom${capitalize(plan.name)}`"
                block
                color="primary"
                rounded
                @click="renewPlan"
              >
                {{ $t('common.extendPlan') }}
              </v-btn>
            </div>
            <div v-else>
              <v-alert text style="color: rgba(0, 0, 0, 0.6);" rounded>
                <div>
                  <span style="font-size: 32px; color: #000000;" class="mr-2">
                    +{{
                      plan.ammountPerAdditional.toLocaleString('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        maximumFractionDigits: 0,
                        minimumFractionDigits: 0
                      })
                    }}
                  </span>
                  <span
                      v-if="
                      plan.ammountPerAdditional !== 0 && $i18n.locale === 'ru'
                    "
                      style="font-size: 24px; font-weight: 300;"
                  >
                    +{{
                      convertFromUsd(
                          plan.ammountPerAdditional,
                          'RUB'
                      ).toLocaleString('ru-RU', {
                        style: 'currency',
                        currency: 'RUB',
                        maximumFractionDigits: 0,
                        minimumFractionDigits: 0
                      })
                    }}
                  </span>
                </div>
                <span style="font-size: 13px;">
                  {{ plan.additional }}
                </span>
              </v-alert>

              <v-btn
                  :id="`buttonTry${capitalize(plan.name)}`"
                  block
                  color="primary"
                  rounded
                  :loading="loading === plan.name"
                  @click="selectPlan(plan.value, plan.count)"
              >
                {{ $t('plans.choosePlan') }}
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
</v-row>

    <footer-terms />
</div>
</template>

<script>
import { mapGetters } from 'vuex'
import { mapFields } from 'vuex-map-fields'
import convertFromUsdMixin from '@/mixins/convertFromUsd'
import prolongPlan from '@/mixins/prolongPlan'
import PLANS_PRICING, { FREE_TARIFF_NAME } from '../../constants/plans'
import FooterTerms from "../FooterTerms";
import {
  ENTERPRISE_TARIFF_NAME
} from "@/constants/plans";
import { CUSTOM_PLANS } from '@/constants/customPlans'

export default {
  name: 'PlansCards',

  components: { FooterTerms },

  mixins: [
    convertFromUsdMixin,
    prolongPlan
  ],

  data() {
    return {
      profilesCountSelected: 1000,
      loading: ''
    }
  },

  computed: {
    ...mapFields('plans', ['paymentInfo']),
    ...mapGetters({
      profile: 'main/profile',
      dialogs: 'plans/dialogs',
      paymentInfo: 'plans/paymentInfo'
    }),
    currentPlanName () {
      const { plan, isCustomTariff } = this.profile.subscription
      const planName = isCustomTariff ? this.$t(`plans.${plan}.${plan}Custom`) : plan

      return  this.$t('plans.heading', [this.capitalizeFirstLetter(planName)])
    },
    profilesCount () {
      return [
        {
          value: 1000,
          text: `1000 ${this.$tc('plans.profilesMultiple', 1000).toLowerCase()}`
        },
        {
          value: 2000,
          text: `2000 ${this.$tc('plans.profilesMultiple', 2000).toLowerCase()}`
        },
        {
          value: 3000,
          text: `3000 ${this.$tc('plans.profilesMultiple', 3000).toLowerCase()}`
        },
        {
          value: 4000,
          text: `4000 ${this.$tc('plans.profilesMultiple', 4000).toLowerCase()}`
        },
        {
          value: 5000,
          text: `5000 ${this.$tc('plans.profilesMultiple', 5000).toLowerCase()}`
        },
        {
          value: 6000,
          text: `6000 ${this.$tc('plans.profilesMultiple', 6000).toLowerCase()}`
        },
        {
          value: 7000,
          text: `7000 ${this.$tc('plans.profilesMultiple', 7000).toLowerCase()}`
        },
        {
          value: 8000,
          text: `8000 ${this.$tc('plans.profilesMultiple', 8000).toLowerCase()}`
        },
        {
          value: 9000,
          text: `9000 ${this.$tc('plans.profilesMultiple', 9000).toLowerCase()}`
        },
        {
          value: 10000,
          text: `10000 ${this.$tc(
              'plans.profilesMultiple',
              10000
          ).toLowerCase()}`
        },
        {
          value: 10000,
          text: `10000 ${this.$tc(
              'plans.profilesMultiple',
              10000
          ).toLowerCase()}`
        }
      ]
    },

    enterprisePrice() {
      let price = 299 + (this.profilesCountSelected.value / 1000 - 1) *
          PLANS_PRICING[ENTERPRISE_TARIFF_NAME].additionalThousandProfiles

      return price ? price : 299
    },

    plans() {
      const plansList = ([
        {
          name: 'Free',
          value:'free',
          ammountPerMonth: 0,
          ammountPerAdditional: 10,
          description: this.$t('plans.free.description'),
          count: 10,
          features: [this.$t('plans.abilityToBuyAdditionalProfiles')],
          additional: this.$t(
              'plans.free.forEvery10AdditionalProfiles'
          ).toLowerCase()
        },
        {
          name: 'Base',
          value:'base',
          ammountPerMonth: 89,
          ammountPerAdditional: 10,
          description: this.$t('plans.base.description'),
          count: 100,
          features: [this.$t('plans.abilityToAddUsersToATeam')],
          additional: this.$t('plans.forAdditionalTeamMember').toLowerCase()
        },
        {
          name: 'Team',
          value:'team',
          ammountPerMonth: 159,
          ammountPerAdditional: 20,
          description: this.$t('plans.team.description'),
          count: 300,
          features: [this.$t('plans.abilityToAddUsersToATeam')],
          additional: this.$t('plans.forAdditionalTeamMember').toLowerCase()
        },
        {
          name: 'Enterprise',
          value:'enterprise',
          ammountPerMonth: this.enterprisePrice,
          ammountPerAdditional: 25,
          description: this.$t('plans.enterprise.description'),
          count: 1000,
          features: [
            this.$t('plans.abilityToAddUsersToATeam'),
            this.$t('plans.abilityToBuyAdditionalProfiles')
          ],
          additional: this.$t('plans.forAdditionalTeamMember').toLowerCase()
        }
      ])

      if (this.profile.subscription?.isCustomTariff && CUSTOM_PLANS[this.profile.subscription.plan]) {
        plansList.unshift(this.getCustomPlanData())
      }

      return plansList
    },
    isFreeTariff () {
      return (planName) => planName.toLowerCase() === FREE_TARIFF_NAME;
    },
  },

  watch: {
    profilesCountSelected(value) {
      if (typeof value != 'object') {
        this.profilesCountSelected = {
          value: this.roundUp(value, 1000),
          text: this.roundUp(value, 1000)
        }
      }
    }
  },

  created() {
    const { tariffName, browsersProfilesCount } = this.$route.query

    if (tariffName && browsersProfilesCount) {
      this.profilesCountSelected = Number(browsersProfilesCount)
      this.selectPlan(tariffName, Number(browsersProfilesCount))
    } else {
      const selectedPlan = this.plans.find(el => el.value === tariffName.toLowerCase())

      this.selectPlan(selectedPlan.value, selectedPlan.count)
    }
  },

  methods: {
    getCustomPlanData() {
      const { plan, users, browserProfiles } = this.profile.subscription

      const ammountPerMonth = this.calculateCustomPlanPrice(plan, users.additionalUsers, browserProfiles.additionalProfiles)

      return {
        name: this.$t(`plans.${plan}.${plan}Custom`),
        ammountPerMonth,
        description: this.$t('plans.freeCustom.description'),
        isCustom: true
      }
    },
    calculateCustomPlanPrice(plan, additionalUsers = 0, additionalProfiles = 0) {
      const { basePrice, additionalUserPrice, additionalProfilePrice } = CUSTOM_PLANS[plan]

      const additionalUsersPrice = additionalUsers * additionalUserPrice
      const additionalProfilesPrice = additionalProfiles * additionalProfilePrice

      return basePrice + additionalUsersPrice + additionalProfilesPrice
    },
    extendPlan () {
      this.$store.dispatch('plans/openDialog', 'updatePlan')
    },
    async selectPlan (plan, profilesCount) {
      this.loading = true
      const updatedPaymentInfo = {}

      updatedPaymentInfo.plan = plan.toLowerCase()

      this.$amplitude.getInstance().logEvent('Click to Choose plan', {
        plan: this.profile.subscription.plan,
        'new plan': this.paymentInfo.plan
      })

      if (plan === ENTERPRISE_TARIFF_NAME) {
        if (this.paymentInfo.profilesCount === 0) {
          updatedPaymentInfo.profilesCount = 1000
        }

        if (typeof this.profilesCountSelected == 'object') {
          updatedPaymentInfo.profilesCount = this.profilesCountSelected.value
        } else {
          updatedPaymentInfo.profilesCount = this.profilesCountSelected
        }
      } else {
        if (plan === FREE_TARIFF_NAME) {
          updatedPaymentInfo.additionalProfiles = 0
        }

        updatedPaymentInfo.profilesCount = profilesCount
      }

      updatedPaymentInfo.additionalUsers = 0
      updatedPaymentInfo.prolongMonths = 1 // Default for 1 month upgrade, if user have available days it changes to 0
      updatedPaymentInfo.paymentMethod = 'upgrade'

      updatedPaymentInfo.additionalUsers = this.profile.subscription.users.limit - 1

      await this.$store.dispatch('plans/updatePaymentInfo', updatedPaymentInfo)

      setTimeout(async () => {
        if (this.profile.subscription.plan === plan && !this.profile.subscription.isCustomTariff) {
          if (
              this.profile.subscription.plan === FREE_TARIFF_NAME &&
              !this.profile.subscription.browserProfiles.additionalProfiles &&
              !this.profile.subscription.users.additionalUsers
          ) {
            await this.$store.dispatch('plans/openDialog', 'paymentFromTrial')

            this.loading = falseadditionalUsers

            return
          }

          await this.$store.dispatch('plans/openDialog', 'updatePlan')
        } else {
          await this.$store.dispatch('plans/openDialog', 'switchPlan')
        }

        this.loading = false
      }, 500)
    },

    capitalize(string) {
      return string.substring(0, 1).toUpperCase() + string.substring(1)
    },

    roundUp(x, factor) {
      return x - (x % factor) + (x % factor > 0 && factor)
    },

    capitalizeFirstLetter(string) {
      return string.charAt(0).toUpperCase() + string.slice(1)
    }
  }
}
</script>

<style lang="scss">
.mt--48px {
  margin-top: -48px;
}

.plan_text {
  color: #b645ee;
  margin-left: 5px;
  margin-right: 5px;
}
</style>
